import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import { ArrowLeft, Camera, X, Tag, MapPin, Upload, CheckCircle, Mail } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 6;

const PostClassified = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Feb 2026 — read ?listingType= from the URL so the composer defaults to the
  // tab the user came from. Sep 2026 — now 5 types incl. swap / sell_swap.
  const initialType = (() => {
    const p = new URLSearchParams(location.search).get('listingType');
    return ['sale', 'swap', 'sell_swap', 'wanted', 'free'].includes(p) ? p : 'sale';
  })();
  const [listingType, setListingType] = useState(initialType);

  const LISTING_TYPE_OPTIONS = [
    { key: 'sale',      label: t('classifieds.typeSellOnly', 'Sell only') },
    { key: 'swap',      label: t('classifieds.typeSwapOnly', 'Swap only') },
    { key: 'sell_swap', label: t('classifieds.typeSellOrSwap', 'Sell or Swap') },
    { key: 'free',      label: t('classifieds.typeFree', 'Free') },
    { key: 'wanted',    label: t('classifieds.typeWanted', 'Wanted') },
  ];
  const SWAP_SUGGESTIONS = ['Garden tools', 'Plants', 'Furniture', 'Bicycle', 'Electrical items', 'Food/produce', 'Other'];
  const isSwap = listingType === 'swap' || listingType === 'sell_swap';
  const showPrice = listingType === 'sale' || listingType === 'sell_swap';

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    location: '',
    swapWanted: [],
    openToOffers: true,
    swapCustom: '',
  });

  const toggleSwapWanted = (val) => {
    setForm(prev => ({
      ...prev,
      swapWanted: prev.swapWanted.includes(val)
        ? prev.swapWanted.filter(x => x !== val)
        : [...prev.swapWanted, val],
    }));
  };
  const addCustomSwap = () => {
    const v = form.swapCustom.trim();
    if (v && !form.swapWanted.includes(v)) {
      setForm(prev => ({ ...prev, swapWanted: [...prev.swapWanted, v], swapCustom: '' }));
    }
  };

  useEffect(() => {
    axios.get(`${API}/api/classifieds/categories`)
      .then(res => setCategories(res.data.categories))
      .catch(() => {});
  }, []);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      setError(t('classifieds.maxPhotos', `Maximum ${MAX_PHOTOS} photos allowed`));
      return;
    }
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/api/upload/classified-photo`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setPhotos(prev => [...prev, res.data.url]);
      }
    } catch (err) {
      // Fallback: use local preview if upload endpoint not available
      for (const file of files) {
        const reader = new FileReader();
        reader.onload = (e) => setPhotos(prev => [...prev, e.target.result]);
        reader.readAsDataURL(file);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    if (!form.title.trim() || form.title.trim().length < 3) return t('classifieds.errTitle', 'Title must be at least 3 characters');
    if (!form.description.trim() || form.description.trim().length < 10) return t('classifieds.errDesc', 'Description must be at least 10 characters');
    if (!form.category) return t('classifieds.errCategory', 'Please select a category');
    if (!form.contactName.trim()) return t('classifieds.errName', 'Please enter your name');
    if (!form.contactEmail.trim() || !form.contactEmail.includes('@')) return t('classifieds.errEmail', 'Please enter a valid email');
    if (!form.location.trim()) return t('classifieds.errLocation', 'Please enter a location');
    if (listingType === 'sell_swap' && (!form.price || parseFloat(form.price) <= 0)) {
      return t('classifieds.errSellSwapPrice', 'Please enter a selling price for a "Sell or Swap" listing');
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        listingType,
        price: showPrice && form.price ? parseFloat(form.price) : null,
        swapWanted: isSwap ? form.swapWanted : [],
        openToOffers: isSwap ? form.openToOffers : null,
        lang: i18n.language,
        photos: photos.filter(p => p.startsWith('http')),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        contactPhone: form.contactPhone.trim() || null,
        location: form.location.trim(),
      };
      const res = await axios.post(`${API}/api/classifieds`, payload);
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('classifieds.errSubmit', 'Failed to post listing. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const categoryIcons = {
    'Tools': '🔧', 'Furniture': '🪑', 'Cars & Vehicles': '🚗', 'Electronics': '📱',
    'Property': '🏠', 'Clothing & Fashion': '👗', 'Home & Garden': '🌿',
    'Sports & Leisure': '⚽', 'Baby & Kids': '👶', 'General': '📦'
  };

  // Success screen
  if (success) {
    const manageUrl = `${window.location.origin}/classifieds/${success.id}?token=${success.editToken}`;
    const copyLink = () => {
      navigator.clipboard.writeText(manageUrl).catch(() => {});
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="max-w-lg mx-auto px-4 pt-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="text-center mb-5">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-8 w-8 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-gray-900 mb-1" data-testid="post-success-title">
                {t('classifieds.posted', 'Item Posted!')}
              </h1>
              <p className="text-sm text-gray-600">
                {t('classifieds.postedDesc', 'Your listing is now live. It will automatically expire after 30 days.')}
              </p>
            </div>

            {/* Email confirmation */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <Mail className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-emerald-800">{t('classifieds.emailSent', 'Manage link sent to your email')}</p>
                <p className="text-xs text-emerald-600 mt-0.5">{t('classifieds.emailSentDesc', 'Check your inbox for a link to edit, update status, or delete your listing.')}</p>
              </div>
            </div>

            {/* Manage link with copy */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-800 font-semibold mb-2">{t('classifieds.saveLink', 'Save this link to manage your listing:')}</p>
              <div className="flex items-center gap-2">
                <p className="text-xs text-amber-700 break-all font-mono flex-1 bg-white/60 rounded px-2 py-1.5">{manageUrl}</p>
                <button onClick={copyLink}
                  className="flex-shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded-lg hover:bg-amber-700 transition-colors"
                  data-testid="copy-manage-link-btn">
                  {copied ? t('linkCopied', 'Copied!') : t('copyLink', 'Copy')}
                </button>
              </div>
            </div>

            {/* What you can do */}
            <div className="bg-gray-50 rounded-lg p-3 mb-5">
              <p className="text-xs font-semibold text-gray-700 mb-2">{t('classifieds.manageHelp', 'With your manage link you can:')}</p>
              <ul className="text-xs text-gray-600 space-y-1.5">
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0"></span>{t('classifieds.manageEdit', 'Edit your listing details')}</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-amber-500 rounded-full flex-shrink-0"></span>{t('classifieds.managePending', 'Set status to Pending Collection')}</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0"></span>{t('classifieds.manageSold', 'Mark as Sold (hidden after 3 days)')}</li>
                <li className="flex items-center gap-2"><span className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>{t('classifieds.manageDelete', 'Delete the listing')}</li>
              </ul>
            </div>

            <div className="flex gap-2">
              <button onClick={() => navigate(`/classifieds/${success.id}?token=${success.editToken}`)}
                className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 text-sm"
                data-testid="view-listing-btn">
                {t('classifieds.manageListing', 'Manage Listing')}
              </button>
              <button onClick={() => navigate('/classifieds')}
                className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 text-sm"
                data-testid="browse-more-btn">
                {t('classifieds.browseMore', 'Browse More')}
              </button>
            </div>
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/classifieds')} className="text-gray-600 hover:text-gray-900" data-testid="back-from-post">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold text-gray-900" data-testid="post-classified-title">
            {t('classifieds.postItem', 'Post an Item')}
          </h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {/* Listing type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('classifieds.listingType', 'Listing type')} *</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="listing-type-selector">
            {LISTING_TYPE_OPTIONS.map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setListingType(opt.key)}
                className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  listingType === opt.key
                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`listing-type-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Photos */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('classifieds.photos', 'Photos')} <span className="text-gray-400 font-normal">({photos.length}/{MAX_PHOTOS})</span>
          </label>
          <div className="flex gap-2 flex-wrap">
            {photos.map((photo, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200">
                <img src={photo} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removePhoto(i)}
                  className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white hover:bg-black/80">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <button type="button" onClick={() => fileInputRef.current?.click()}
                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
                data-testid="add-photo-btn" disabled={uploading}>
                {uploading ? (
                  <div className="w-5 h-5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Camera className="h-5 w-5" />
                    <span className="text-[10px] mt-0.5">{t('classifieds.addPhoto', 'Add')}</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
            onChange={handlePhotoUpload} data-testid="photo-file-input" />
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('classifieds.itemTitle', 'Title')} *</label>
          <input type="text" value={form.title} onChange={(e) => handleChange('title', e.target.value)}
            placeholder={t('classifieds.titlePlaceholder', 'What are you selling?')}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            maxLength={120} data-testid="classified-title-input" />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('classifieds.category', 'Category')} *</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => handleChange('category', cat)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                  form.category === cat
                    ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
                data-testid={`post-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                <span>{categoryIcons[cat] || '📦'}</span>
                <span className="truncate">{t(`classifieds.cat.${cat}`, cat)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Price — only for Sell only / Sell or Swap */}
        {showPrice && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('classifieds.price', 'Price')} {listingType === 'sell_swap' ? '*' : <span className="text-gray-400 font-normal">({t('classifieds.optional', 'optional')})</span>}</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">€</span>
            <input type="number" value={form.price} onChange={(e) => handleChange('price', e.target.value)}
              placeholder="0.00" min="0" step="0.01"
              className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              data-testid="classified-price-input" />
          </div>
          <p className="text-xs text-gray-400 mt-1">{listingType === 'sell_swap' ? t('classifieds.sellSwapPriceHint', 'Buyers can buy at this price OR make a swap offer.') : t('classifieds.priceHint', 'Leave empty for "Negotiable"')}</p>
        </div>
        )}

        {/* Swap preferences — only for Swap only / Sell or Swap */}
        {isSwap && (
        <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-3" data-testid="swap-fields">
          <label className="block text-sm font-semibold text-teal-900">🔄 {t('classifieds.swapWantedTitle', 'What would you like in exchange?')}</label>
          <div className="flex flex-wrap gap-2">
            {SWAP_SUGGESTIONS.map(s => (
              <button
                key={s}
                type="button"
                onClick={() => toggleSwapWanted(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  form.swapWanted.includes(s)
                    ? 'bg-teal-600 text-white border-teal-600'
                    : 'bg-white text-teal-700 border-teal-300 hover:border-teal-500'
                }`}
                data-testid={`swap-suggestion-${s.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                {s}
              </button>
            ))}
          </div>
          {/* custom items chosen */}
          {form.swapWanted.filter(s => !SWAP_SUGGESTIONS.includes(s)).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="swap-custom-chips">
              {form.swapWanted.filter(s => !SWAP_SUGGESTIONS.includes(s)).map(s => (
                <span key={s} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-teal-600 text-white">
                  {s}
                  <button type="button" onClick={() => toggleSwapWanted(s)}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input type="text" value={form.swapCustom} onChange={(e) => handleChange('swapCustom', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSwap(); } }}
              placeholder={t('classifieds.swapAddOwn', 'Add your own…')}
              className="flex-1 px-3 py-2 border border-teal-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
              data-testid="swap-custom-input" />
            <button type="button" onClick={addCustomSwap} className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium" data-testid="swap-custom-add">{t('add', 'Add')}</button>
          </div>
          <label className="flex items-center gap-2 text-sm text-teal-900 cursor-pointer" data-testid="open-to-offers-row">
            <input type="checkbox" checked={form.openToOffers} onChange={(e) => handleChange('openToOffers', e.target.checked)} className="h-4 w-4 rounded border-teal-400 text-teal-600" data-testid="open-to-offers-checkbox" />
            {t('classifieds.openToOffers', 'Open to offers')}
          </label>
          <p className="text-xs text-teal-700/80">{t('classifieds.swapPrefsHint', 'These are preferences only — people can still offer something different.')}</p>
        </div>
        )}

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('classifieds.descLabel', 'Description')} *</label>
          <textarea value={form.description} onChange={(e) => handleChange('description', e.target.value)}
            placeholder={t('classifieds.descPlaceholder', 'Describe the item, condition, features...')}
            rows={4} maxLength={2000}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
            data-testid="classified-desc-input" />
          <p className="text-xs text-gray-400 mt-1">{form.description.length}/2000</p>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <MapPin className="h-3.5 w-3.5 inline mr-1" />{t('classifieds.location', 'Location')} *
          </label>
          <input type="text" value={form.location} onChange={(e) => handleChange('location', e.target.value)}
            placeholder={t('classifieds.locationPlaceholder', 'e.g. Almería Centro, Retamar...')}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
            data-testid="classified-location-input" />
        </div>

        {/* Contact info */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{t('classifieds.contactInfo', 'Your Contact Details')}</h3>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.yourName', 'Name')} *</label>
            <input type="text" value={form.contactName} onChange={(e) => handleChange('contactName', e.target.value)}
              placeholder={t('classifieds.namePlaceholder', 'Your name')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              data-testid="classified-name-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.yourEmail', 'Email')} *</label>
            <input type="email" value={form.contactEmail} onChange={(e) => handleChange('contactEmail', e.target.value)}
              placeholder={t('classifieds.emailPlaceholder', 'your@email.com')}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              data-testid="classified-email-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('classifieds.yourPhone', 'Phone')} <span className="text-gray-400 font-normal">({t('classifieds.optional', 'optional')})</span></label>
            <input type="tel" value={form.contactPhone} onChange={(e) => handleChange('contactPhone', e.target.value)}
              placeholder="+34 612 345 678"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              data-testid="classified-phone-input" />
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="post-error-msg">
            {error}
          </div>
        )}

        {/* Submit */}
        <button type="submit" disabled={submitting}
          className="w-full py-3 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 transition-colors text-sm"
          data-testid="submit-classified-btn">
          {submitting ? t('classifieds.posting', 'Posting...') : t('classifieds.postForFree', 'Post for Free')}
        </button>

        <p className="text-center text-xs text-gray-400 pb-4">
          {t('classifieds.autoExpire', 'Listings automatically expire after 30 days')}
        </p>
      </form>

      <BottomNavigation />
    </div>
  );
};

export default PostClassified;
